import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { Company, User, AuthState, LoginForm, RegisterForm, ApiResponse } from '../types';
import { prepareDemo } from '../data/seed';

export type RegisterResult = ApiResponse<User>;

const AuthContext = createContext<AuthState & {
  login: (data: LoginForm) => Promise<ApiResponse<User>>;
  register: (data: RegisterForm) => Promise<RegisterResult>;
  resendVerification: (email: string) => Promise<RegisterResult>;
  changePassword: (email: string, currentPassword: string, newPassword: string) => Promise<RegisterResult>;
  requestPasswordReset: (email: string) => Promise<RegisterResult>;
  resetPassword: (token: string, newPassword: string) => Promise<RegisterResult>;
  demoLogin: () => User;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
} | undefined>(undefined);

// Base de la API. En produccion la sirve el propio servidor (mismo origen).
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api';

// Cache local de usuarios (para el panel SuperAdmin y datos de perfil).
const MOCK_USERS_KEY = 'reformall_users';
const MOCK_SESSION_KEY = 'reformall_session';
const COMPANIES_KEY = 'reformall_all_companies';

const getMockUsers = (): User[] => {
  try {
    const data = localStorage.getItem(MOCK_USERS_KEY);
    if (data) return JSON.parse(data);
  } catch {}
  return [];
};

const saveMockUsers = (users: User[]) => {
  localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(users));
};

const upsertLocalUser = (user: User) => {
  const users = getMockUsers();
  const idx = users.findIndex(u => u.id === user.id || u.email.toLowerCase() === user.email.toLowerCase());
  if (idx >= 0) users[idx] = { ...users[idx], ...user };
  else users.push(user);
  saveMockUsers(users);
};

const SUPERADMIN: User = {
  id: 'superadmin-0001',
  email: 'admin@sistema.com',
  name: 'Superadministrador',
  role: 'superadmin',
  companyId: null,
  password: 'Abcd1234',
  emailVerified: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

const seedMockUsers = (): User[] => {
  const users = getMockUsers();
  if (users.some(u => u.email === SUPERADMIN.email)) return users;
  const seeded = [SUPERADMIN, ...users];
  saveMockUsers(seeded);
  return seeded;
};

const generateId = () => crypto.randomUUID();

/** Crea la empresa local del usuario la primera vez que entra (los datos de la
 *  empresa viven en el navegador; la cuenta la gestiona el servidor). */
const provisionCompany = (companyId: string | null, companyName: string | null | undefined, ownerId: string) => {
  if (!companyId) return;
  if (localStorage.getItem(`reformall_${companyId}_company`)) return;
  const now = new Date().toISOString();
  const company: Company = {
    id: companyId,
    name: companyName || '',
    vatRate: 21,
    createdAt: now,
    updatedAt: now,
    ownerId,
  };
  localStorage.setItem(`reformall_${companyId}_company`, JSON.stringify(company));
  let all: Company[] = [];
  try {
    const raw = localStorage.getItem(COMPANIES_KEY);
    all = raw ? JSON.parse(raw) : [];
  } catch {
    all = [];
  }
  if (!all.some(c => c.id === companyId)) {
    localStorage.setItem(COMPANIES_KEY, JSON.stringify([...all, company]));
  }
};

async function postJson(path: string, body: unknown): Promise<{ ok: boolean; status: number; data: any }> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: { error: 'No se pudo conectar con el servidor' } };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
    isAuthenticated: false,
  });

  useEffect(() => {
    const initAuth = () => {
      try {
        seedMockUsers();

        // Entrada en modo demo: ?demo=1 (boton de la web personal).
        const params = new URLSearchParams(window.location.search);
        if (params.get('demo') === '1') {
          const user = prepareDemo();
          const token = `session_${generateId()}`;
          localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify({ user, token }));
          sessionStorage.removeItem(MOCK_SESSION_KEY);
          params.delete('demo');
          const qs = params.toString();
          window.history.replaceState(
            {},
            '',
            window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
          );
          setState({ user, token, isLoading: false, isAuthenticated: true });
          return;
        }

        const session = localStorage.getItem(MOCK_SESSION_KEY) || sessionStorage.getItem(MOCK_SESSION_KEY);
        if (session) {
          const { user, token } = JSON.parse(session);
          setState({ user, token, isLoading: false, isAuthenticated: true });
        } else {
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } catch {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };
    initAuth();
  }, []);

  const persistSession = useCallback((user: User, rememberMe: boolean) => {
    const token = `session_${generateId()}`;
    const store = rememberMe ? localStorage : sessionStorage;
    (rememberMe ? sessionStorage : localStorage).removeItem(MOCK_SESSION_KEY);
    store.setItem(MOCK_SESSION_KEY, JSON.stringify({ user, token }));
    setState({ user, token, isLoading: false, isAuthenticated: true });
    return token;
  }, []);

  const demoLogin = useCallback((): User => {
    const user = prepareDemo();
    persistSession(user, true);
    return user;
  }, [persistSession]);

  const login = useCallback(async (data: LoginForm): Promise<ApiResponse<User>> => {
    const email = data.email.trim().toLowerCase();

    // SuperAdmin: cuenta local para la gestion de usuarios de la aplicacion.
    const superadmin = getMockUsers().find(u => u.role === 'superadmin');
    const superadminEmail = superadmin?.email ?? SUPERADMIN.email;
    if (email === superadminEmail.toLowerCase() && (!superadmin?.password || superadmin.password === data.password)) {
      const account = superadmin ?? SUPERADMIN;
      persistSession(account, data.rememberMe);
      return { data: account, error: null, success: true };
    }

    const { ok, data: payload } = await postJson('/login', { email, password: data.password });
    if (!ok) {
      return { data: null, error: payload?.error || 'No se pudo iniciar sesión', success: false };
    }

    const apiUser = payload.user as { id: string; email: string; name: string; companyId: string | null; companyName?: string | null };
    const now = new Date().toISOString();
    const user: User = {
      id: apiUser.id,
      email: apiUser.email,
      name: apiUser.name,
      role: 'admin',
      companyId: apiUser.companyId,
      emailVerified: true,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    };

    provisionCompany(user.companyId, apiUser.companyName, user.id);
    upsertLocalUser(user);
    persistSession(user, data.rememberMe);
    return { data: user, error: null, success: true };
  }, [persistSession]);

  const register = useCallback(async (data: RegisterForm): Promise<RegisterResult> => {
    const { ok, data: payload } = await postJson('/registrations', {
      name: data.name,
      email: data.email,
      password: data.password,
      companyName: data.companyName,
    });
    if (!ok) {
      return { data: null, error: payload?.error || 'No se pudo completar el registro', success: false };
    }
    return { data: null, error: null, success: true };
  }, []);

  const resendVerification = useCallback(async (email: string): Promise<RegisterResult> => {
    const { ok, data: payload } = await postJson('/registrations/resend', { email });
    if (!ok) {
      return { data: null, error: payload?.error || 'No se pudo reenviar la solicitud', success: false };
    }
    return { data: null, error: null, success: true };
  }, []);

  const changePassword = useCallback(async (email: string, currentPassword: string, newPassword: string): Promise<RegisterResult> => {
    const { ok, data: payload } = await postJson('/password/change', { email, currentPassword, newPassword });
    if (!ok) {
      return { data: null, error: payload?.error || 'No se pudo cambiar la contraseña', success: false };
    }
    return { data: null, error: null, success: true };
  }, []);

  const requestPasswordReset = useCallback(async (email: string): Promise<RegisterResult> => {
    const { ok, data: payload } = await postJson('/password/forgot', { email });
    if (!ok) {
      return { data: null, error: payload?.error || 'No se pudo enviar la solicitud', success: false };
    }
    return { data: null, error: null, success: true };
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string): Promise<RegisterResult> => {
    const { ok, data: payload } = await postJson('/password/reset', { token, newPassword });
    if (!ok) {
      return { data: null, error: payload?.error || 'No se pudo restablecer la contraseña', success: false };
    }
    return { data: null, error: null, success: true };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(MOCK_SESSION_KEY);
    sessionStorage.removeItem(MOCK_SESSION_KEY);
    setState({ user: null, token: null, isLoading: false, isAuthenticated: false });
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setState(prev => {
      if (!prev.user) return prev;
      const updated = { ...prev.user, ...updates, updatedAt: new Date().toISOString() } as User;
      upsertLocalUser(updated);
      const storage = localStorage.getItem(MOCK_SESSION_KEY) ? localStorage : sessionStorage;
      storage.setItem(MOCK_SESSION_KEY, JSON.stringify({ user: updated, token: prev.token }));
      return { ...prev, user: updated };
    });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, resendVerification, changePassword, requestPasswordReset, resetPassword, demoLogin, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
